/**
 * useEdgeAssociations - Hook para obtener asociaciones de un edge
 *
 * @remarks
 * Proporciona datos de objetos asociados a un segmento de red,
 * agrupados por tipo de entidad. Útil para mostrar en el panel
 * de detalles del edge.
 *
 * @see tracking/GRAMATICA_ASOCIACIONES_PUNTO_RED.md
 */

import { useState, useEffect, useCallback } from 'react';
import {
  pointAssociationApi,
  type EdgeAssociationsResponse,
  type AssociationSummary,
} from '../services/point-association-api.service';

interface UseEdgeAssociationsOptions {
  /** Si false, no carga automáticamente */
  enabled?: boolean;
}

interface UseEdgeAssociationsReturn {
  /** Datos de asociaciones */
  data: EdgeAssociationsResponse | null;
  /** Estado de carga */
  isLoading: boolean;
  /** Error si ocurrió */
  error: Error | null;
  /** Recargar datos */
  refetch: () => Promise<void>;
}

/**
 * Hook para obtener asociaciones de un edge
 */
export function useEdgeAssociations(
  projectId: string | null | undefined,
  edgeId: string | null | undefined,
  options: UseEdgeAssociationsOptions = {},
): UseEdgeAssociationsReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<EdgeAssociationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId || !edgeId || !enabled) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.getEdgeAssociations(projectId, edgeId);
      setData(result);
    } catch (err) {
      console.error('[useEdgeAssociations] Error fetching associations:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch associations'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, edgeId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

/**
 * Hook para obtener asociaciones de un node
 */
export function useNodeAssociations(
  projectId: string | null | undefined,
  nodeId: string | null | undefined,
  options: UseEdgeAssociationsOptions = {},
): UseEdgeAssociationsReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<EdgeAssociationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId || !nodeId || !enabled) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.getNodeAssociations(projectId, nodeId);
      // Transform to match EdgeAssociationsResponse format for consistency
      setData({
        edgeId: result.nodeId,
        total: result.total,
        byEntityType: result.byEntityType,
        associations: result.associations,
      });
    } catch (err) {
      console.error('[useNodeAssociations] Error fetching associations:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch associations'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, nodeId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

export type { EdgeAssociationsResponse, AssociationSummary };
